/**
 * useSurcharges Hook
 *
 * Fetches surcharges from the Rails API for use in price modifier dropdowns.
 * Defaults to per_page=100 (Listable max) so the full catalog is available
 * up-front for client-side filtering.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { surchargesQueryKeys, getSurcharges } from '../api/surcharges';
import type { SurchargesListResponse, FetchSurchargesParams } from '../api/surcharges';

const STALE_TIME = 30_000;
const DEFAULT_PER_PAGE = 100;

interface UseSurchargesOptions {
  params?: FetchSurchargesParams;
  initialData?: SurchargesListResponse;
}

export function useSurcharges({ params, initialData }: UseSurchargesOptions = {}) {
  const mergedParams: FetchSurchargesParams = { per_page: DEFAULT_PER_PAGE, ...params };

  return useQuery({
    queryKey: surchargesQueryKeys.list(mergedParams),
    queryFn: () => getSurcharges(mergedParams),
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
    ...(initialData ? { initialData, initialDataUpdatedAt: Date.now() } : {}),
  });
}
